import React from 'react';
import { Users, BarChart3, Settings, X, ClipboardList, FileText, MailPlus } from 'lucide-react';

// 1. Define el tipo de las secciones del admin
export type AdminSection = 'users' | 'reports' | 'invoices' | 'statistics' | 'settings' | 'invitations';

// 2. Define las props
interface MenuAdminProps {
  activeSection: AdminSection;
  setActiveSection: (section: AdminSection) => void;
  isMobileMenuOpen: boolean;
  setIsMobileMenuOpen: (open: boolean) => void;
}

const MenuAdmin: React.FC<MenuAdminProps> = ({
  activeSection,
  setActiveSection,
  isMobileMenuOpen,
  setIsMobileMenuOpen
}) => {
  // 3. Configuración del menú admin
  const menuItems = [
    {
      id: 'users' as AdminSection,
      name: 'Gestión de Usuarios',
      icon: Users,
      description: 'Administrar usuarios del sistema'
    },
    {
      id: 'reports' as AdminSection,
      name: 'Reportes de Clínicas',
      icon: ClipboardList,
      description: 'Revisar y aprobar reportes subidos'
    },
    {
      id: 'invoices' as AdminSection,
      name: 'Facturas e Informes',
      icon: FileText,
      description: 'Subir facturas e informes a los clientes'
    },
    {
      id: 'invitations' as AdminSection,
      name: 'Invitaciones',
      icon: MailPlus,
      description: 'Invitar nuevos clientes al sistema'
    },
    {
      id: 'statistics' as AdminSection,
      name: 'Estadísticas',
      icon: BarChart3,
      description: 'Ver métricas y reportes'
    },
    {
      id: 'settings' as AdminSection,
      name: 'Configuración',
      icon: Settings,
      description: 'Configuración del sistema'
    }
  ];

  return (
    <>
      {/* Sidebar Menu - Desktop */}
      <aside className="hidden lg:block w-80 bg-white rounded-lg shadow-lg p-4 h-fit sticky top-8">
        <h2 className="text-lg font-semibold text-gray-800 mb-4">Menú de Administración</h2>
        <nav className="space-y-2">
          {menuItems.map((item) => {
            const Icon = item.icon;
            const isActive = activeSection === item.id;

            return (
              <button
                key={item.id}
                onClick={() => setActiveSection(item.id)}
                className={`w-full flex items-start gap-3 p-4 rounded-lg transition-all ${
                  isActive
                    ? 'bg-gradient-to-r from-[#144A7B] to-[#0d3354] text-white shadow-md'
                    : 'bg-gray-50 text-gray-700 hover:bg-gray-100'
                }`}
              >
                <Icon size={24} className={isActive ? 'text-white' : 'text-[#144A7B]'} />
                <div className="text-left">
                  <div className={`font-semibold ${isActive ? 'text-white' : 'text-gray-800'}`}>
                    {item.name}
                  </div>
                  <div className={`text-sm ${isActive ? 'text-blue-100' : 'text-gray-500'}`}>
                    {item.description}
                  </div>
                </div>
              </button>
            );
          })}
        </nav>

        {/* Info Box */}
        <div className="mt-6 p-4 bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg border border-blue-200">
          <h3 className="font-semibold text-blue-900 mb-2">Panel de Admin</h3>
          <p className="text-sm text-blue-800">
            Gestiona el sistema completo desde aquí.
          </p>
        </div>
      </aside>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="lg:hidden fixed inset-0 bg-black bg-opacity-50 z-50">
          <div className="bg-white w-80 h-full p-6 shadow-xl">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-semibold text-gray-800">Menú Admin</h2>
              <button
                onClick={() => setIsMobileMenuOpen(false)}
                className="p-2 hover:bg-gray-100 rounded-lg transition"
              >
                <X size={24} />
              </button>
            </div>

            <nav className="space-y-2">
              {menuItems.map((item) => {
                const Icon = item.icon;
                const isActive = activeSection === item.id;

                return (
                  <button
                    key={item.id}
                    onClick={() => {
                      setActiveSection(item.id);
                      setIsMobileMenuOpen(false);
                    }}
                    className={`w-full flex items-start gap-3 p-4 rounded-lg transition-all ${
                      isActive
                        ? 'bg-gradient-to-r from-[#144A7B] to-[#0d3354] text-white shadow-md'
                        : 'bg-gray-50 text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    <Icon size={24} className={isActive ? 'text-white' : 'text-[#144A7B]'} />
                    <div className="text-left">
                      <div className={`font-semibold ${isActive ? 'text-white' : 'text-gray-800'}`}>
                        {item.name}
                      </div>
                      <div className={`text-sm ${isActive ? 'text-blue-100' : 'text-gray-500'}`}>
                        {item.description}
                      </div>
                    </div>
                  </button>
                );
              })}
            </nav>

            <div className="mt-6 p-4 bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg border border-blue-200">
              <h3 className="font-semibold text-blue-900 mb-2">Panel de Admin</h3>
              <p className="text-sm text-blue-800">
                Gestiona el sistema completo desde aquí.
              </p>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default MenuAdmin;
